/**
 * Chart overlays: shapes drawn on top of a chart by something other than the
 * chart itself.
 *
 * This application already draws overlays of its own - voyage leg bands, engine
 * changeover lanes, the threshold lines a numeric filter implies. Those are
 * drawn by the plot code because the plot code is what knows about them. This
 * registry is for the other case: a **feature** that wants to say something on a
 * chart it does not own.
 *
 * What the registry knows is **shapes**: a horizontal line at a value, a band
 * across a range of x, a mark at a point. It has no idea what any of them mean,
 * and deliberately no vocabulary for rules, alerts, thresholds or limits - a
 * provider computes meaning and hands over geometry. That is the seam that lets
 * a feature which decorates charts be deleted in one directory, and it is why
 * this file is in `state/` rather than inside any feature.
 *
 * Providers are asked on every render, synchronously, and must be cheap: they
 * are called during a draw, on a chart that may be one of several in a grid.
 * A provider that needs to fetch something fetches it elsewhere, keeps it, and
 * answers from what it has - returning nothing until it has an answer, which is
 * also the honest thing to draw when you do not know.
 */

/** A horizontal line at one value on one y scale. */
export interface OverlayLine {
  kind: 'line';
  /** The value, in the units of `scale`. */
  value: number;
  /** A uPlot scale key - `'y'` or `'y2'` here. Ignored if the chart has no such scale. */
  scale: string;
  label: string;
  /** A CSS colour, or a `var(--token)` the chart resolves. Defaults to the overlay colour. */
  color?: string;
  /** `dashed` by default: an overlay is drawn on top of somebody's data. */
  style?: 'solid' | 'dashed';
}

/** A vertical band between two x values - a period on a time axis. */
export interface OverlayBand {
  kind: 'band';
  /** Seconds since the epoch on a time axis, or an x value on a numeric one. */
  from: number;
  to: number;
  label?: string;
  color?: string;
}

/** A single point of interest. */
export interface OverlayMark {
  kind: 'mark';
  x: number;
  /** Omitted pins the mark to the top of the plot area. */
  y?: number;
  scale?: string;
  label?: string;
  color?: string;
}

export type OverlayShape = OverlayLine | OverlayBand | OverlayMark;

/**
 * What a provider is told about the chart it is being asked for.
 *
 * Only facts the chart already has. No selection state, no request object, no
 * component: a provider must not be able to reach back into the plot and change
 * it, and the shape of this context is what enforces that.
 */
export interface OverlayContext {
  /** `'line'`, `'scatter'`, ... - the plot type as the rest of the app names it. */
  plotType: string;
  /** Which entities are drawn. Ship ids today; the provider maps them to its own world. */
  entityIds: readonly string[];
  /** Column names on the left and right axes, in the order they are drawn. */
  left: readonly string[];
  right: readonly string[];
  /** True when x is a timestamp, which is what makes a band meaningful. */
  xIsTime: boolean;
  /** The x range currently drawn, in the x axis's own units. */
  xFrom: number;
  xTo: number;
}

export interface OverlayProvider {
  /** Stable id, for replacing a provider rather than stacking two of them. */
  id: string;
  /** Shapes for this chart, or `[]` for nothing to say. Must not throw. */
  shapes: (context: OverlayContext) => OverlayShape[];
}

const providers = new Map<string, OverlayProvider>();

/** Register a provider, replacing one with the same id. */
export function registerOverlayProvider(provider: OverlayProvider): void {
  providers.set(provider.id, provider);
}

/** Forget a provider. The charts stop showing its shapes on the next render. */
export function unregisterOverlayProvider(id: string): void {
  providers.delete(id);
}

/**
 * Every shape for one chart, from every provider.
 *
 * A provider that throws is dropped for this render and the chart is drawn
 * without it: a decoration must never be able to take the data down with it.
 */
export function collectOverlayShapes(context: OverlayContext): OverlayShape[] {
  if (!providers.size) return [];
  const out: OverlayShape[] = [];
  for (const provider of providers.values()) {
    try {
      const shapes = provider.shapes(context);
      if (shapes && shapes.length) out.push(...shapes);
    } catch {
      // Deliberately silent and deliberately not fatal. A provider is a guest
      // on somebody else's chart.
    }
  }
  return out;
}

/** Test seam: drop every provider. */
export function clearOverlayProvidersForTests(): void {
  providers.clear();
}
